import { upperFirst, isString, isNil } from "lodash-es";
import moment from "moment";
import ICatalog_Item from "../models/ICatalog_Item";
import ICatalog_Item_Vendor_Source_Map from "../models/ICatalog_Item_Vendor_Source_Map";
import IVendor_Source from "../models/IVendor_Source";
import ISupplierModel from "../models/ISupplierModel";
import IPrice_Group from "../models/IPrice_Group";
import IOrder_Item from "../models/IOrder_Item";
import IKit_Builder_Data from "../models/IKit_Builder_Data";
import IPrice_Band_Compiled from "../models/IPrice_Band_Compiled";
import IPrice_Band from "../models/IPrice_Band";
import IWarehouse from "@models/IWarehouse";
import IUserUI from "../models/IUserUI";
import IVendorModel from "../models/IVendorModel";
import RestEndpointsEnum from "../models/RestEndpointsEnum";
import { buildRestUrlWithQuery, buildAuthHeadersForFetch } from "./RestService";
import IOnTypeaheadCatalogItemDTO from "../models/DTO/IOnTypeaheadCatalogItemDTO";
import ITypeaheadCatalogItemRestDTO from "../models/DTO/ITypeaheadCatalogItemRestDTO";

export const getVendorSourceExpectedDate = (vendorSource: IVendor_Source, item: ICatalog_Item): string => {
    if (vendorSource) {
        let leadTime = vendorSource.default_lead_time;
        let leadTimeType = vendorSource.default_lead_time_type;

        let itemMap: ICatalog_Item_Vendor_Source_Map | undefined;

        if (item?.Catalog_Item_Vendor_Source_Maps) {
            for (const mapItem of item.Catalog_Item_Vendor_Source_Maps) {
                if (mapItem.vendor_source_id === vendorSource.id) {
                    itemMap = mapItem;
                    break;
                }
            }
        }

        if (itemMap) {
            if (itemMap.lead_time && itemMap.lead_time_type) {
                leadTime = itemMap.lead_time;
                leadTimeType = itemMap.lead_time_type;
            }
        }

        const leadTimeEstimatedDate = moment().add(leadTime, leadTimeType).format("YYYY-MM-DD");

        return leadTimeEstimatedDate;
    } else {
        return "";
    }
};

export const getLeadTime = (vendorSource: IVendor_Source, item: ICatalog_Item): string => {
    let leadTime = vendorSource.default_lead_time;
    let leadTimeType = vendorSource.default_lead_time_type;

    let itemMap: ICatalog_Item_Vendor_Source_Map | undefined;

    if (item.Catalog_Item_Vendor_Source_Maps) {
        for (const mapItem of item.Catalog_Item_Vendor_Source_Maps) {
            if (mapItem.vendor_source_id === vendorSource.id) {
                itemMap = mapItem;
                break;
            }
        }
    }

    if (itemMap) {
        if (itemMap.lead_time && itemMap.lead_time_type) {
            leadTime = itemMap.lead_time;
            leadTimeType = itemMap.lead_time_type;
        }
    }

    return leadTime + " " + upperFirst(leadTimeType);
};

export const getVendorSourceMinOrderQty = (vendorSource: IVendor_Source, item: ICatalog_Item): number => {
    let minQty = 1;

    if (item?.Catalog_Item_Vendor_Source_Maps && vendorSource) {
        for (const mapItem of item.Catalog_Item_Vendor_Source_Maps) {
            if (mapItem.vendor_source_id === vendorSource.id) {
                if (mapItem.min_order_qty) {
                    minQty = mapItem.min_order_qty;
                }

                break;
            }
        }
    }

    return Number(minQty);
};

/**
 * If the catalog item has a pack qty, but the pricing is not priced per pack, but per item, get the order qty.
 *
 * @param catalogItem
 * @param qtyInput
 */
export const getPackItemOrderQty = (catalogItem: ICatalog_Item, qtyInput: number) => {
    let qty = qtyInput;

    if (catalogItem.has_pack_qty) {
        if (catalogItem.pack_qty && catalogItem.pack_qty >= 1 && !catalogItem.price_per_pack) {
            qty = catalogItem.pack_qty * qtyInput;
        }
    }

    return qty;
};

export const getItemQtyOnHandBasedOnMaterials = (catalogItem: ICatalog_Item): number => {
    let qtyOnHand = 0;

    if (catalogItem.Material_Catalog_Item_Maps?.length) {
        const maxItemByMaterials: {
            material: string;
            maxQty: number;
        }[] = [];

        for (const materialMap of catalogItem.Material_Catalog_Item_Maps) {
            const material = materialMap.Material!;
            const materialQtyInItem = materialMap.qty;
            const materialQty = material?.qty_on_hand || 0;

            const maxQty = materialQty / materialQtyInItem;

            maxItemByMaterials.push({ material: material?.name, maxQty });
        }

        let maxQtyOnHandBasedOnMaterial: number | undefined;

        for (const maxItemByMaterial of maxItemByMaterials) {
            if (isNil(maxQtyOnHandBasedOnMaterial)) {
                maxQtyOnHandBasedOnMaterial = maxItemByMaterial.maxQty;
            } else {
                if (maxQtyOnHandBasedOnMaterial && maxQtyOnHandBasedOnMaterial > maxItemByMaterial.maxQty) {
                    maxQtyOnHandBasedOnMaterial = maxItemByMaterial.maxQty;
                }
            }
        }

        if (maxQtyOnHandBasedOnMaterial) {
            qtyOnHand = maxQtyOnHandBasedOnMaterial;
        }
    }

    return qtyOnHand;
};

export const getCatalogItemDescription = (
    supplierModel: ISupplierModel,
    catalogItem: ICatalog_Item,
    priceGroupModel?: IPrice_Group,
    orderItem?: IOrder_Item
): string | undefined => {
    let description = catalogItem.description;

    if (supplierModel.has_pricing_descriptions) {
        if (priceGroupModel) {
            const pricingDescriptions = catalogItem.pricing_descriptions;

            if (
                pricingDescriptions &&
                isString(pricingDescriptions[priceGroupModel.value]) &&
                pricingDescriptions[priceGroupModel.value].length
            ) {
                description = pricingDescriptions[priceGroupModel.value];
            }
        }
    }

    if (orderItem) {
        if (orderItem.description) {
            description = orderItem.description;
        }
    }

    return description;
};

/**
 * Determine if the kit builder selections are complete for the given catalog item and selections.
 *
 * @param catalogItem
 * @param selections
 */
export const isKitBuilderSelectionsComplete = (
    catalogItem: ICatalog_Item,
    selections: IKit_Builder_Data
): { isComplete: boolean; issues: string[] } => {
    let isComplete = true;
    const issues: string[] = [];

    if (catalogItem.Kit_Builder_Options) {
        for (const option of catalogItem.Kit_Builder_Options) {
            const minRequired = option.min_required || 0;
            const selectionsLength = (selections[option.name]?.selections || []).length || 0;

            if (minRequired > selectionsLength) {
                isComplete = false;
                issues.push(`${option.name} requires ${minRequired} selections, found ${selectionsLength}`);
            }

            if (option.Kit_Builder_Option_Items && selections[option.name]) {
                for (const optionItem of option.Kit_Builder_Option_Items) {
                    if (optionItem.require_sub_option) {
                        // locate the option item in the selections and see if it has any sub options.
                        const selection = selections[option.name].selections.find((_selection) => {
                            return _selection.optionItem.id === optionItem.id;
                        });

                        if (selection && !(selection.subOptionSelections || []).length) {
                            isComplete = false;
                            issues.push(`${option.name} option item ${optionItem.Catalog_Item?.name} requires a sub option selection`);
                        }
                    }
                }
            }
        }
    }

    return { isComplete, issues };
};

/**
 * Check if a quantity falls within this price band's range
 *
 * @param {number} qty - The quantity to check
 * @returns {boolean} True if quantity is within band's range, false otherwise
 */
const containsQty = (qty: number, priceBand: IPrice_Band | IPrice_Band_Compiled) => {
    return qty >= priceBand.start_qty && (!priceBand.end_qty || qty <= priceBand.end_qty);
};

export const findPriceBand = (qty: number, priceBands: IPrice_Band_Compiled[]): IPrice_Band | null => {
    const match = priceBands.find((band) => {
        return containsQty(qty, band);
    });

    if (match) {
        const priceBand = match.priceBand ? match.priceBand : (match as IPrice_Band);

        return priceBand || null;
    } else {
        return null;
    }
};

const getQtyOnHandWarehouseMapItem = (catalogItem: ICatalog_Item, shippingWarehouse?: IWarehouse) => {
    return catalogItem.Warehouse_Catalog_Items_Maps?.find((map) => map.warehouse_id === shippingWarehouse?.id);
};

const typeaheadCatalogItems = async (
    catalogGuid: string,
    searchPhrase: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel,
    options?: Omit<ITypeaheadCatalogItemRestDTO, "name">
): Promise<IOnTypeaheadCatalogItemDTO> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.CatalogTypeaheadItems,
        params: { catalogGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ name: searchPhrase, ...options }),
    });

    if (!response.ok) {
        throw new Error(`Failed to typeahead catalog items: ${response.statusText}`);
    }

    return (await response.json()) as IOnTypeaheadCatalogItemDTO;
};

const CatalogItemService = {
    getVendorSourceExpectedDate,
    getLeadTime,
    getVendorSourceMinOrderQty,
    getPackItemOrderQty,
    getItemQtyOnHandBasedOnMaterials,
    getCatalogItemDescription,
    isKitBuilderSelectionsComplete,
    findPriceBand,
    getQtyOnHandWarehouseMapItem,
    typeaheadCatalogItems,
};

export default CatalogItemService;
